import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon/Icon';
import Typography from '@/components/atoms/Typography/Typography';
import { cn } from '@/helpers/cn';
import { createId } from '@/helpers/createId';
import { dataLayerPush } from '@/helpers/dataLayer';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { forwardRef, useCallback } from 'react';
const ServiceFeatureCard = forwardRef(({ headingLevel = 'h3', title, text, link, icon, animated = true, onKeyUp, testId = 'service-feature-card', motionProps, stretch = false, parentComponent, }, ref) => {
    const handleKeyUp = useCallback((event) => {
        if (onKeyUp) {
            onKeyUp(event);
        }
    }, []);
    const titleId = createId(title, 'title');
    const textId = createId(text, 'text');
    const linkId = createId(link?.text, 'link');
    const labelledByIds = [title ? titleId : undefined, text ? textId : undefined, link ? linkId : undefined]
        .filter(Boolean)
        .join(' ');
    const content = (_jsxs("div", { className: "flex h-full flex-col gap-2 p-1.25 pb-2 sm:min-h-[298px] sm:p-2 md:min-w-[277px]", ...(animated ? { 'data-animate-fade': true } : {}), "data-testid": testId, children: [icon && (_jsx(Icon, { testid: `${testId}-icon`, variant: icon, className: "h-3 w-3 text-symbol-secondary group-hover/button:text-border-tertiary" })), _jsx(Typography, { variant: "heading-sm", as: headingLevel, "data-testid": `${testId}-title`, id: titleId, children: title }), text && (_jsx("div", { className: "rich-text", "data-testid": `${testId}-text`, id: textId, dangerouslySetInnerHTML: { __html: text } })), link && (_jsx(Button, { mode: "non-interactive", variant: link.linkDisplay, icon: link.icon, className: cn(link.linkDisplay !== 'solid' && 'group-hover/button:text-border-tertiary', 'mt-auto justify-start text-left dark-a:text-text-body dark-b:text-text-body'), children: ({ icon, ...buttonProps }) => (_jsxs("span", { ...buttonProps, "data-testid": `${testId}-button`, id: linkId, className: cn(buttonProps.className, 'inline-flex items-center gap-x-0.5 text-left'), children: [link.text, " ", icon] })) }))] }));
    const buttonClasses = clsx('group/button focus-inset-2 bg-surface-secondary hover:outline-border-tertiary hover:outline', stretch && 'block flex-1');
    if (link) {
        const MotionA = motion.a;
        return (_jsx(MotionA, { ref: ref, onKeyUp: handleKeyUp, onClick: () => {
                dataLayerPush('serviceClick', {
                    service_type: icon,
                    service: title,
                    click_text: link.text,
                });
                dataLayerPush('ctaInteraction', {
                    cta_type: parentComponent,
                    click_text: link.text,
                    click_url: link.url,
                });
                dataLayerPush('accordionClick', {
                    accordion_interaction: stretch ? 'collapse' : 'expand',
                    accordion_type: parentComponent ?? '',
                    accordion_title: title,
                });
            }, href: link.url, target: link.target, className: buttonClasses, "aria-labelledby": labelledByIds, ...motionProps, children: content }));
    }
    const MotionDiv = motion.div;
    return (_jsx(MotionDiv, { ref: ref, ...motionProps, className: buttonClasses, "aria-labelledby": labelledByIds, children: content }));
});
ServiceFeatureCard.displayName = 'ServiceFeatureCard';
export default ServiceFeatureCard;
